import { useContext, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ControlledInputSelect } from '@components/controlled-input-select';
import { Divider } from '@components/divider';
import { FIELD_QUANTITY, REQUIRED_TEXT_ERROR } from '@components/dynamic-input-fields/constants';
import { IconBoxOffice } from '@components/icons/icon-box-office';
import { InputCounter } from '@components/input-counter';
import { InputSelectAddons } from '@components/input-select-addons';
import { InputSelectAddonsMode } from '@components/input-select-addons/input-select-addons.type';
import { InputSelectAdmission } from '@components/input-select-admission';
import { ModalFooter } from '@components/modals/modal-footer';
import { ModalHeader } from '@components/modals/modal-header';
import { MultiselectOption } from '@components/multiselect/multiselect.type';
import { BoxOfficeForm } from '@components/process-box-office/process-box-office.type';
import { SORT_ASC } from '@constants/global';
import { IMPORT_CONTEXT } from '@constants/query-params';
import { CompanyAuthContext } from '@contexts/company-auth-context/company-auth-context';
import { CompanyClubContext } from '@contexts/company-club/company-club-context';
import { Admission } from '@domain/admission';
import { ActiveChannel } from '@domain/channel';
import { EventListItem } from '@domain/event';
import { useGetOfferAddon } from '@hooks/addons/use-get-offer-addons';
import { useGetAdmissions } from '@hooks/admissions/use-get-admissions';
import { AdmissionResponse } from '@hooks/admissions/use-get-admissions.type';
import { useGetChannels } from '@hooks/events/use-get-event-channels';
import { useGetEventList } from '@hooks/events/use-get-event-list';
import { useGetActiveChannels } from '@hooks/use-active-channels';
import { calculateChannelMaxQuantityPerTransaction } from '@utils/calculate-max-items-per-transation/calculate-channel-max-quantity-per-transaction';
import { formatDateName } from '@utils/date-time-format/format-date-name';
import moment from 'moment';

import { filterByRole } from '../utils/filter-by-role';
import { shouldFilterOfferIfIsSiae } from '../utils/filter-offer-if-siae';

import { SelectTypeSkeleton } from './select-type-skeleton';

export const SelectType = ({ onContinue, offer }: { onContinue: () => void; offer?: Admission }) => {
  const { t, i18n } = useTranslation();
  const {
    companyClubState: { isSiaeImportLimited },
    companyState: { id: companyId },
    clubState: {
      id: clubId,
      city: { timezone },
    },
  } = useContext(CompanyClubContext);
  const { role } = useContext(CompanyAuthContext);
  const {
    formState: { isValid },
    watch,
    setValue,
  } = useFormContext<BoxOfficeForm>();

  const [channelList, setChannelList] = useState<ActiveChannel[]>([]);
  const [selectedAddons, setSelectedAddons] = useState<MultiselectOption[]>([]);

  const eventId = watch('eventId');
  const offerId = watch('offerId');
  const channelId = watch('channelId');
  const todayNow = useMemo(() => moment().tz(timezone).unix(), [timezone]);

  const {
    data: eventList,
    isLoading: isEventListLoading,
    hasNextPage,
    fetchNextPage,
  } = useGetEventList({
    companyId,
    clubId,
    view: 'all',
    params: {
      startTime: todayNow,
      sort: SORT_ASC,
      orderBy: 'date',
      limit: 50,
    },
  });
  const eventListFiltered = (eventList?.pages as EventListItem[][] | undefined)?.map((page) =>
    page.filter((event) => !event.settings?.isHidden)
  );
  const { data: admissionList } = useGetAdmissions({
    eventId,
    context: IMPORT_CONTEXT,
    options: { enabled: !!eventId },
  });
  const { data: addOnsList } = useGetOfferAddon({ offerId, enabled: !!offerId });
  const availableAddons = addOnsList?.filter((addon) => {
    if (!addon.quantity) return true;

    return addon.soldItems < addon.quantity;
  });
  const { data: activeChannels } = useGetActiveChannels({
    offerId,
    enabled: !!offerId,
  });
  const { data: eventChannels } = useGetChannels({ eventId, enabled: !!eventId && !!offerId });

  const filterCondition = (selectedOffer: Admission) => shouldFilterOfferIfIsSiae(selectedOffer, isSiaeImportLimited);
  const filterOfferByRole = (selectedOffer: Admission) =>
    filterByRole({ role: role?.name, isHidden: selectedOffer.settings.isHidden });
  const filterNonNominative = (selectedOffer: Admission) => !selectedOffer.settings.isNominative;
  const filterSiaeApproved = (selectedOffer: Admission) => !selectedOffer.settings.italyRegulation?.isSiaeApproved;

  // Filter offers based on various conditions - now we support addons so no need to filter mandatory addons
  const filteredAdmissionList = {
    bottleService:
      admissionList?.bottleService.filter(
        (bottle) =>
          filterCondition(bottle) &&
          filterOfferByRole(bottle) &&
          filterNonNominative(bottle) &&
          filterSiaeApproved(bottle)
      ) ?? [],
    guestList:
      admissionList?.guestList.filter(
        (guest) =>
          filterCondition(guest) && filterOfferByRole(guest) && filterNonNominative(guest) && filterSiaeApproved(guest)
      ) ?? [],
    ticket:
      admissionList?.ticket.filter(
        (ticket) =>
          filterCondition(ticket) &&
          filterOfferByRole(ticket) &&
          filterNonNominative(ticket) &&
          filterSiaeApproved(ticket)
      ) ?? [],
  };

  const showAddons = !!offer && offer.addOns.length > 0;

  const handleEventSelectorChange = () => {
    if (
      filteredAdmissionList.bottleService.length === 0 &&
      filteredAdmissionList.ticket.length === 0 &&
      filteredAdmissionList.guestList.length === 1
    ) {
      setValue('offerId', filteredAdmissionList.guestList[0].id, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    } else {
      setValue('offerId', '', { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    }
  };

  const handleOfferChange = () => {
    const isDefaultChannelOnList = channelList.some((channel) => channel.id === channelId);
    setValue('addOns', [], { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    setValue('channelId', isDefaultChannelOnList ? channelId : '', {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });

    setSelectedAddons([]);
  };

  useEffect(() => {
    if (eventChannels && activeChannels && offer) {
      const channelsFormatted = eventChannels
        .filter((channel) => channel.isEnabled)
        .map((channel) => ({
          ...channel.channel,
          maxQuantity: null,
        }));

      if (activeChannels.isForAllChannels) {
        setChannelList(channelsFormatted);
        return;
      }

      setChannelList(activeChannels.channels);
    }
  }, [activeChannels, eventChannels, offer]);

  if (hasNextPage) fetchNextPage();

  return (
    <>
      <ModalHeader title={t('offers:boxOfficeTitle')} primaryIcon={IconBoxOffice} />
      <div className='flex flex-col gap-4 px-4 py-6 sm:px-20 sm:pb-12'>
        {isEventListLoading ? (
          <SelectTypeSkeleton />
        ) : (
          <>
            {!!eventListFiltered && (
              <ControlledInputSelect
                fieldRegisterName='eventId'
                options={eventListFiltered.flat().map((e: EventListItem) => ({
                  ...e,
                  name: formatDateName(e.name, e.startingTime, timezone, i18n.language),
                }))}
                rules={{ required: { message: t(REQUIRED_TEXT_ERROR), value: true } }}
                onChange={handleEventSelectorChange}
                label={t('offers:selectAnEvent')}
              />
            )}
            {!!admissionList && (
              <InputSelectAdmission
                admissionList={filteredAdmissionList as AdmissionResponse}
                eventId={eventId}
                selectedOffer={offer}
                onChange={handleOfferChange}
              />
            )}
            {showAddons && offer && (
              <InputSelectAddons
                addOnsList={availableAddons ?? []}
                selectedAddons={selectedAddons}
                required={offer.hasMandatoryAddOns}
                mode={offer.addOnChoiceMode as InputSelectAddonsMode}
                setSelectedAddons={setSelectedAddons}
              />
            )}
            {offer && (
              <ControlledInputSelect
                fieldRegisterName='channelId'
                rules={{ required: { message: t(REQUIRED_TEXT_ERROR), value: true } }}
                options={channelList}
                // isDisabled={offer && channelList.length === 1}
                label={t('invitations:processModalChannelSelectorTitle')}
              />
            )}
            {offer && channelId && (
              <>
                <Divider className='my-2' />
                <InputCounter
                  formCounterName={FIELD_QUANTITY}
                  minQuantity={offer.itemsPerTransaction.min ?? 0}
                  maxQuantity={calculateChannelMaxQuantityPerTransaction(offer)}
                  title={t('invitations:totalGuestCounterText')}
                  error={t('errors:minQuantityFieldError', { number: offer.itemsPerTransaction?.min })}
                />
              </>
            )}
          </>
        )}
      </div>
      <ModalFooter
        isPrimaryDisabled={isEventListLoading || !isValid}
        primaryButtonText={t('offers:continueButton')}
        onPrimaryClick={onContinue}
      />
    </>
  );
};
